/**
 * Pure code synthesis for every manifest id (15 §4.1 / §5.1 P0: UI, pen
 * motif, stingers and foley stand-ins ship as parameters, not files). No
 * Math.random — a xorshift seeded from (id, variation) so the same variation
 * renders identically everywhere. Output is peak-normalised to −1 dBTP then
 * trimmed by the manifest `gainDb`.
 */
import { AUDIO_MUSIC_BPM, AUDIO_STEM_BARS, dbToGain } from './constants.js'
import { SOUNDS, type SoundId, type SynthName } from './manifest.js'
import { euclid, renderPattern, shift, stepsFrom, stepsFromMask, type Step, type Track } from './sequencer.js'
import { PRESETS, addInto, biquad, bowedDrone, burst, onePoleLp, pulse, ring } from './voices.js'

export interface RenderedSound { channels: Float32Array[]; loopStart?: number; loopEnd?: number }

// ── tiny DSP kit ────────────────────────────────────────────────────────────

class Rng {
  constructor(private s: number) { if (this.s === 0) this.s = 0x9e3779b9 }
  next(): number { let x = this.s; x ^= x << 13; x ^= x >>> 17; x ^= x << 5; this.s = x >>> 0; return this.s / 4294967296 }
  range(a: number, b: number): number { return a + (b - a) * this.next() }
  bip(): number { return this.next() * 2 - 1 }
}

function hash(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) } return h >>> 0 }

interface Ctx { sr: number; rng: Rng; v: number }

const TAU = Math.PI * 2
const buf = (c: Ctx, s: number): Float32Array => new Float32Array(Math.max(1, Math.round(s * c.sr)))

/** Exponential decay envelope at sample i. */
const decay = (i: number, sr: number, t: number): number => Math.exp(-i / (sr * t))
/** Attack-decay: linear attack `a` s then exp decay `d` s. */
const ad = (i: number, sr: number, a: number, d: number): number => { const t = i / sr; return t < a ? t / a : Math.exp(-(t - a) / d) }

/** One-pole lowpass/highpass as stateful closures. */
function lp(sr: number, hz: number): (x: number) => number { let y = 0; const k = 1 - Math.exp(-TAU * hz / sr); return (x) => (y += k * (x - y)) }
function hp(sr: number, hz: number): (x: number) => number { const l = lp(sr, hz); return (x) => x - l(x) }
/** Resonant-ish bandpass: two-pole SVF. */
function bp(sr: number, hz: number, q: number): (x: number) => number {
  let low = 0, band = 0
  const f = 2 * Math.sin(Math.PI * Math.min(0.45, hz / sr))
  const d = 1 / Math.max(0.5, q)
  return (x) => { low += f * band; const high = x - low - d * band; band += f * high; return band }
}

function noiseBurst(c: Ctx, len: number, atk: number, dec: number, hz: number, q: number, amp = 1): Float32Array {
  const out = buf(c, len); const f = bp(c.sr, hz, q)
  for (let i = 0; i < out.length; i++) out[i] = f(c.rng.bip()) * ad(i, c.sr, atk, dec) * amp
  return out
}

function partials(c: Ctx, len: number, parts: { hz: number; amp: number; dec: number }[], atk = 0.001): Float32Array {
  const out = buf(c, len)
  for (const p of parts) { const w = TAU * p.hz / c.sr; const ph0 = c.rng.next() * TAU; for (let i = 0; i < out.length; i++) out[i] += Math.sin(ph0 + w * i) * p.amp * ad(i, c.sr, atk, p.dec) }
  return out
}

function tone(c: Ctx, len: number, hz: number, atk: number, dec: number, shape: 'sine' | 'tri' = 'sine', amp = 1): Float32Array {
  const out = buf(c, len); const w = TAU * hz / c.sr
  for (let i = 0; i < out.length; i++) { const ph = (w * i) % TAU; const s = shape === 'sine' ? Math.sin(ph) : 1 - 4 * Math.abs(ph / TAU - 0.5); out[i] = s * ad(i, c.sr, atk, dec) * amp }
  return out
}

/** Sum layers, offsetting each by `at` seconds. */
function mix(c: Ctx, len: number, layers: { at?: number; sig: Float32Array; gain?: number }[]): Float32Array {
  const out = buf(c, len)
  for (const l of layers) { const o = Math.round((l.at ?? 0) * c.sr); const g = l.gain ?? 1; for (let i = 0; i < l.sig.length && o + i < out.length; i++) out[o + i] += l.sig[i] * g }
  return out
}

function reverse(a: Float32Array): Float32Array { const o = new Float32Array(a.length); for (let i = 0; i < a.length; i++) o[i] = a[a.length - 1 - i]; return o }

/** Detune by semitones (playback-rate style resample). */
function semis(c: Ctx, n: number): number { return Math.pow(2, n / 12) * (1 + 0.01 * c.rng.bip()) }

// ── struck-metal / material helpers (A4 palette, built on voices.ts) ────────────

const rn = (c: Ctx) => () => c.rng.next()

/** Noise through a bandpass whose centre sweeps hz0 → hz1 over `sweepS` (SVF, per-sample coefficient). */
function sweepNoise(c: Ctx, lenS: number, hz0: number, hz1: number, sweepS: number, q: number, atk: number, dec: number, amp = 1): Float32Array {
  const out = buf(c, lenS)
  let low = 0, band = 0
  const d = 1 / Math.max(0.5, q)
  for (let i = 0; i < out.length; i++) {
    const t = i / c.sr
    const hz = hz0 + (hz1 - hz0) * Math.min(1, t / sweepS)
    const f = 2 * Math.sin(Math.PI * Math.min(0.2, hz / c.sr))
    const x = c.rng.bip()
    low += f * band; const high = x - low - d * band; band += f * high
    out[i] = band * ad(i, c.sr, atk, dec) * amp
  }
  return out
}

/** Modal partials whose pitch glides by `ratio` (ease-out) after `holdS`, over `glideS`; all partials share one decay so the spectral mix is stable while it rises. */
function glideMetal(c: Ctx, baseHz: number, parts: { ratio: number; amp: number }[], decayS: number, lenS: number, holdS: number, glideS: number, ratio: number): Float32Array {
  const out = buf(c, lenS)
  for (const p of parts) {
    let ph = 0
    for (let i = 0; i < out.length; i++) {
      const t = i / c.sr
      const u = Math.max(0, Math.min(1, (t - holdS) / glideS))
      const m = 1 + (ratio - 1) * (1 - Math.pow(1 - u, 3))
      ph += TAU * baseHz * p.ratio * m / c.sr
      out[i] += Math.sin(ph) * p.amp * ad(i, c.sr, 0.0006, decayS)
    }
  }
  return out
}

/** Damped composite-on-composite clank: unipolar click + a metallic ring 8 dB down (discs are composite, not bells). */
function clank(c: Ctx, hz: number, amp = 1): Float32Array {
  const out = buf(c, 0.12)
  addInto(out, pulse(c.sr, hz, amp), 0)
  addInto(out, ring(c.sr, hz, 0.014, 0.3 * amp, 0, 0.0002, 0.08), pulse(c.sr, hz).length)
  addInto(out, ring(c.sr, hz * 1.62, 0.009, 0.2 * amp, 0, 0.0002, 0.06), pulse(c.sr, hz).length)
  return out
}

/** Struck-metal note for the broadcast stingers: bell preset at `note` semitones from A5 (880 Hz). */
const bellNote = (c: Ctx, note: number, vel = 1): Float32Array => PRESETS.bell(c.sr, vel, note, rn(c))
const taiko = (c: Ctx, vel = 1): Float32Array => PRESETS.kick(c.sr, vel, 0, rn(c))
/** Short struck-metal tick (count_tick, UI-adjacent): deterministic phases so the semitone steps are ordered. */
function metalTick(c: Ctx, hz: number, lenS = 0.3): Float32Array {
  const out = buf(c, lenS)
  addInto(out, ring(c.sr, hz, 0.04, 1, 0, 0.0004, lenS), 0)
  addInto(out, ring(c.sr, hz * 2.76, 0.008, 0.35, 0, 0.0004, lenS), 0)
  addInto(out, ring(c.sr, hz * 5.4, 0.005, 0.15, 0, 0.0004, lenS), 0)
  addInto(out, pulse(c.sr, 3600, 0.5), 0)
  return out
}

// ── the plotter-pen motif (08 §1) ──────────────────────────────────────────

/** Forward pen: servo whine (FM sine sweep) + nib scratch (bp noise) + N stroke clicks. */
function penForward(c: Ctx, len: number, strokes: number, amp = 1): Float32Array {
  const out = buf(c, len); const N = out.length
  const scratch = bp(c.sr, 3200, 2.5); const whineLp = lp(c.sr, 4000)
  let ph = 0
  for (let i = 0; i < N; i++) {
    const t = i / N
    const hz = 900 + 700 * t + 120 * Math.sin(TAU * 7 * i / c.sr)
    ph += TAU * hz / c.sr
    const env = Math.sin(Math.PI * Math.min(1, t * 1.05)) // swell
    out[i] = whineLp(Math.sin(ph)) * 0.25 * env + scratch(c.rng.bip()) * 0.35 * env
  }
  for (let s = 0; s < strokes; s++) {
    const at = Math.round(N * (0.08 + 0.84 * (s + 0.5) / strokes))
    const click = noiseBurst(c, 0.012, 0.0005, 0.003, 5000 + c.rng.range(-800, 800), 1.2, 0.9)
    for (let i = 0; i < click.length && at + i < N; i++) out[at + i] += click[i]
  }
  for (let i = 0; i < N; i++) out[i] *= amp
  return out
}

/** Reversed pen: high-pass sweeping up, strokes clicking off. */
function penReverse(c: Ctx, len: number, strokes: number): Float32Array {
  const fwd = reverse(penForward(c, len, strokes))
  const out = new Float32Array(fwd.length)
  let y = 0
  for (let i = 0; i < fwd.length; i++) { const hz = 300 + 5000 * (i / fwd.length); const k = 1 - Math.exp(-TAU * hz / c.sr); y += k * (fwd[i] - y); out[i] = fwd[i] - y }
  return out
}

/** 600 ms un-draw: pen reversed, ending in three slow dimension-tick clicks. */
function derezUndraw(c: Ctx): Float32Array {
  const pen = penReverse(c, 0.44, 9)
  const ticks = [0.46, 0.52, 0.58].map((at) => ({ at, sig: noiseBurst(c, 0.02, 0.0005, 0.006, 4200, 2, 0.8) }))
  // Servo ticks every 55 ms under the sweep at −18 dB; a 200 ms low "paper drag" as the sheet is pulled.
  const servo: { at: number; sig: Float32Array; gain: number }[] = []
  for (let at = 0.02; at < 0.42; at += 0.055) servo.push({ at, sig: PRESETS.tick(c.sr, 0.6, 0, rn(c)), gain: 0.125 * 0.6 })
  const drag = burst(c.sr, 0.2, 0.02, 0.06, onePoleLp(c.sr, 300), rn(c), 1.6)
  return mix(c, 0.62, [{ sig: pen }, ...servo, { sig: drag, gain: 0.35 }, ...ticks])
}

// ── stems (08 §6 bed, 15 §4.2) — composed 8-bar patterns through the sequencer ────────

/** Loop point 25 ms ahead of beat 1: the seam falls on decaying tails, never on a downbeat hit. */
const STEM_LEAD_IN_S = 0.025
const stemOpts = (c: Ctx, id: string, bars = AUDIO_STEM_BARS) => ({ sr: c.sr, bars, bpm: AUDIO_MUSIC_BPM, seed: hash(id), leadInS: STEM_LEAD_IN_S })
const at = (bar: number, beat: number, sub: number, vel: number, note?: number): Step => (note === undefined ? { beat: bar * 4 + beat, sub, vel } : { beat: bar * 4 + beat, sub, vel, note })
const bars8 = (pat: string): Step[] => stepsFrom(pat.repeat(8))

/** stem_tick — quiet mechanism: every 8th, accents on 1 and 3. */
function stemTick(c: Ctx): Float32Array {
  return renderPattern([{ inst: PRESETS.tick, steps: bars8('X.x.x.x.X.x.x.x.'), gain: 0.5 }], stemOpts(c, 'stem_tick'))
}
/** stem_pulse_low — taiko on 1 of each bar, the "and" of 3 in bars 4 and 8. */
function stemPulseLow(c: Ctx): Float32Array {
  const steps: Step[] = []
  for (let b = 0; b < 8; b++) { steps.push(at(b, 0, 0, 1)); if (b === 3 || b === 7) steps.push(at(b, 2, 2, 0.8)) }
  return renderPattern([{ inst: PRESETS.kick, steps, gain: 0.8 }], stemOpts(c, 'stem_pulse_low'))
}
/** stem_kit — kick 1 & 3, anvil 2 & 4 (vel 0.6), euclid(5,16) tick ghosts. */
function stemKit(c: Ctx): Float32Array {
  return renderPattern([
    { inst: PRESETS.kick, steps: stepsFrom('X.......X.......'), gain: 0.9 },
    { inst: PRESETS.anvil, steps: stepsFrom('....x.......x...', 0.6 / 0.7), gain: 0.9 },
    { inst: PRESETS.tick, steps: stepsFromMask(euclid(5, 16), 0.35), gain: 0.9 },
  ], stemOpts(c, 'stem_kit'))
}
/** stem_metal — bell on the tresillo, pitch alternating a fifth per bar. */
function stemMetal(c: Ctx): Float32Array {
  const tres = euclid(3, 8)
  const steps: Step[] = []
  for (let b = 0; b < 2; b++) tres.forEach((on, e) => { if (on) steps.push(at(b, Math.floor(e / 2), (e % 2) * 2, e === 0 ? 1 : 0.75, b === 0 ? 0 : 7)) })
  return renderPattern([{ inst: PRESETS.bell, steps, gain: 0.6 }], stemOpts(c, 'stem_metal'))
}
/** stem_low_hits — tom on beat 1 of bars 1, 3, 5, 7 (48 Hz) and beat 3.5 of bars 4, 8 (52 Hz). */
function stemLowHits(c: Ctx): Float32Array {
  const up = 12 * Math.log2(52 / 48)
  const steps = [0, 2, 4, 6].map((b) => at(b, 0, 0, 1, 0)).concat([3, 7].map((b) => at(b, 2, 2, 0.85, up)))
  return renderPattern([{ inst: PRESETS.tom, steps, gain: 1.0 }], stemOpts(c, 'stem_low_hits'))
}
/** stem_ride — every 16th, euclid(7,16) accents. */
function stemRide(c: Ctx): Float32Array {
  const acc = euclid(7, 16)
  const steps = acc.map((a, k) => ({ beat: Math.floor(k / 4), sub: k % 4, vel: a ? 1 : 0.5 }))
  return renderPattern([{ inst: PRESETS.ride, steps, gain: 0.4 }], stemOpts(c, 'stem_ride'))
}
/** stem_hub — looser bed: euclid(5,16) ticks, a bell every second bar. */
function stemHub(c: Ctx): Float32Array {
  return renderPattern([
    { inst: PRESETS.tick, steps: stepsFromMask(euclid(5, 16), 0.4), gain: 0.5 },
    { inst: PRESETS.bell, steps: [at(1, 2, 2, 0.6, 0)], gain: 0.35 },
  ], stemOpts(c, 'stem_hub'))
}
/** stem_results — 12 bars (30 s): tick every beat, a resolved I–V–I bell figure every 4 bars. */
function stemResults(c: Ctx): Float32Array {
  const figure = [at(0, 0, 0, 0.8, 0), at(1, 2, 0, 0.7, 7), at(3, 0, 0, 0.9, 0)]
  return renderPattern([
    { inst: PRESETS.tick, steps: stepsFrom('x...x...x...x...'), gain: 0.5 },
    { inst: PRESETS.bell, steps: [...figure, ...shift(figure, 16), ...shift(figure, 32)], gain: 0.4 },
  ], stemOpts(c, 'stem_results', 12))
}
/** stem_drone — 45 s bowed drone at 55 Hz, filter opening at 20 s and 35 s (non-looping). */
function stemDrone(c: Ctx): Float32Array {
  return bowedDrone(c.sr, 45, [{ atS: 0, cutoffHz: 400 }, { atS: 20, cutoffHz: 1200 }, { atS: 35, cutoffHz: 3200 }], () => c.rng.next(), 55)
}

// ── recipes ────────────────────────────────────────────────────────────────

const RECIPES: Record<SynthName, (c: Ctx) => Float32Array> = {
  silence: (c) => buf(c, 0.05),
  thwip: (c) => {
    // Air thwip: noise sweeping 1.2 → 4 kHz over 90 ms (variations start 1.0–1.6 kHz) + wrist snap + 60 ms whir onset.
    const start = (1000 + 200 * c.v) * semis(c, 0)
    const air = sweepNoise(c, 0.2, start, 4000, 0.09, 1.6, 0.003, 0.035, 1)
    const snap = buf(c, 0.02); addInto(snap, pulse(c.sr, 2000, 0.8), 0); addInto(snap, ring(c.sr, 2000, 0.002, 0.5, 0, 0.0002, 0.012), pulse(c.sr, 2000).length)
    const whir = burst(c.sr, 0.06, 0.02, 0.02, biquad(c.sr, 'bp', 520, 4), rn(c), 1.2)
    return mix(c, 0.22, [{ sig: air }, { sig: snap }, { at: 0.02, sig: whir, gain: 0.4 }])
  },
  whir_loop: (c) => { const n = Math.round(2 * c.sr); const out = new Float32Array(n); const f = bp(c.sr, 520, 4); for (let i = 0; i < n; i++) out[i] = f(c.rng.bip()) * 0.6 + Math.sin(TAU * 130 * i / c.sr) * 0.2 * (1 + 0.2 * Math.sin(TAU * 3 * i / c.sr)); return out },
  whir_trem_loop: (c) => { const n = Math.round(2 * c.sr); const out = new Float32Array(n); const f = bp(c.sr, 900, 5); for (let i = 0; i < n; i++) out[i] = (f(c.rng.bip()) * 0.7) * (0.55 + 0.45 * Math.sin(TAU * 8 * i / c.sr)); return out },
  slap: (c) => mix(c, 0.2, [
    { sig: burst(c.sr, 0.04, 0.0005, 0.01, biquad(c.sr, 'bp', 600 * semis(c, 0), 1.2), rn(c), 1.6) },   // concrete slap
    { sig: pulse(c.sr, 900, 0.5) },                                                                      // contact click
    { sig: ring(c.sr, 1700 * semis(c, 0), 0.03, 0.25, 0, 0.0005, 0.12) },                                 // faint metal ring, −12 dB
  ]),
  ring_steel: (c) => mix(c, 0.5, [
    { sig: burst(c.sr, 0.005, 0.0003, 0.0015, biquad(c.sr, 'bp', 2500, 2), rn(c), 1.2) },               // 5 ms strike
    { sig: ring(c.sr, 1710 * semis(c, 0), 0.09, 1, c.rng.next() * TAU, 0.0006, 0.5) },                    // two partials, 300 ms audible
    { sig: ring(c.sr, 2890 * semis(c, 0), 0.07, 0.6, c.rng.next() * TAU, 0.0006, 0.5) },
  ]),
  tick_tight: (c) => noiseBurst(c, 0.05, 0.0004, 0.006, 5200, 2.5),
  thud_pad: (c) => mix(c, 0.35, [{ sig: noiseBurst(c, 0.2, 0.002, 0.04, 320, 0.8) }, { sig: tone(c, 0.3, 85, 0.002, 0.08, 'sine', 0.7) }]),
  ping_pen: (c) => {
    // Signature: 6 ms pen click → struck metal (880 base, partials 1/2.76/5.4) held 60 ms then gliding up a fifth over 250 ms → pen flick as the disc is re-drawn.
    const click = burst(c.sr, 0.006, 0.0003, 0.0015, biquad(c.sr, 'bp', 3000, 4), rn(c), 1.4)
    const metal = glideMetal(c, 880 * semis(c, 0), [{ ratio: 1, amp: 1 }, { ratio: 2.76, amp: 0.35 }, { ratio: 5.4, amp: 0.15 }], 0.3, 0.8, 0.06, 0.25, 1.5)
    return mix(c, 0.8, [{ sig: click }, { at: 0.004, sig: metal, gain: 0.8 }, { at: 0.42, sig: penForward(c, 0.16, 2, 0.3) }])
  },
  clank_double: (c) => {
    // Two composite clanks 35 ms apart, the second a semitone lower, with a little grit scatter.
    const hz = 1400 * semis(c, 0)
    return mix(c, 0.35, [{ sig: clank(c, hz) }, { at: 0.035, sig: clank(c, hz * Math.pow(2, -1 / 12), 0.9) }, { at: 0.05, sig: burst(c.sr, 0.15, 0.005, 0.05, biquad(c.sr, 'bp', 3000, 0.8), rn(c), 0.12) }])
  },
  thump_body: (c) => {
    // Padded body thump: 90 Hz sine gliding down 8 st over 120 ms with a little drive, then a cloth rustle from 100 ms.
    const out = buf(c, 0.35)
    let ph = 0
    for (let i = 0; i < out.length; i++) { const t = i / c.sr; ph += TAU * 90 * Math.pow(2, -8 / 12 * Math.min(1, t / 0.12)) / c.sr; out[i] = Math.tanh(1.6 * Math.sin(ph) * ad(i, c.sr, 0.003, 0.1)) }
    addInto(out, burst(c.sr, 0.06, 0.005, 0.02, biquad(c.sr, 'bp', 2500, 1), rn(c), 0.35), Math.round(0.1 * c.sr))
    return out
  },
  clack: (c) => noiseBurst(c, 0.08, 0.0005, 0.012, 2600, 2),
  snap_glove: (c) => mix(c, 0.15, [
    { sig: burst(c.sr, 0.018, 0.0005, 0.005, biquad(c.sr, 'bp', 1800 * semis(c, 0), 3), rn(c), 2) },      // glove snap
    { at: 0.012, sig: burst(c.sr, 0.08, 0.01, 0.025, onePoleLp(c.sr, 900), rn(c), 1.2), gain: 0.4 },    // leather creak tail
  ]),
  drop_rattle: (c) => { const hits = [0, 0.06, 0.1, 0.13].map((at, i) => ({ at, sig: noiseBurst(c, 0.06, 0.0008, 0.012, 1500 - i * 200, 1.3, 1 - i * 0.2) })); return mix(c, 0.35, [...hits, { at: 0.12, sig: noiseBurst(c, 0.2, 0.01, 0.06, 900, 0.8, 0.3) }]) },
  scoop: (c) => noiseBurst(c, 0.18, 0.02, 0.05, 1100, 0.9, 0.6),
  two_note_down: (c) => mix(c, 0.35, [{ sig: tone(c, 0.15, 880, 0.002, 0.05, 'tri') }, { at: 0.12, sig: tone(c, 0.2, 660, 0.002, 0.07, 'tri') }]),
  pen_fwd_short: (c) => penForward(c, 0.3, 4),
  pen_rev_short: (c) => penReverse(c, 0.25, 3),
  paper_tick: (c) => noiseBurst(c, 0.04, 0.0004, 0.008, 3800, 1.8),
  nib_click: (c) => noiseBurst(c, 0.03, 0.0003, 0.005, 6000, 2.2),
  step: (c) => {
    // Six concrete footfalls: heel (LP 400 Hz, 25 ms) and scuff (BP 1.5 kHz, 40–70 ms) in a different mix per variation.
    const heel = burst(c.sr, 0.025, 0.001, 0.008, onePoleLp(c.sr, 400 * (0.85 + 0.06 * c.v)), rn(c), 2.2)
    const scuff = burst(c.sr, 0.04 + 0.006 * c.v, 0.003, 0.012 + 0.002 * c.v, biquad(c.sr, 'bp', 1500 * semis(c, 0), 1), rn(c), 1)
    return mix(c, 0.15, [{ sig: heel, gain: 1 - 0.08 * (c.v % 3) }, { at: 0.006 + 0.002 * c.v, sig: scuff, gain: 0.35 + 0.12 * (c.v % 4) }])
  },
  cloth: (c) => noiseBurst(c, 0.08, 0.01, 0.02, 2000, 0.6, 0.5),
  scuff: (c) => mix(c, 0.3, [{ sig: noiseBurst(c, 0.25, 0.005, 0.06, 1300 * semis(c, 0), 0.7) }, { sig: noiseBurst(c, 0.2, 0.02, 0.05, 500, 0.8, 0.5) }]),
  plant: (c) => mix(c, 0.2, [{ sig: noiseBurst(c, 0.1, 0.001, 0.02, 350, 0.8) }, { at: 0.035, sig: noiseBurst(c, 0.1, 0.001, 0.02, 300, 0.8, 0.8) }]),
  jump_scuff: (c) => {
    // S10 take-off: heel leaving the slab (LP 420 Hz) under a heel scuff dragging 1.1 → 1.6 kHz (per-variation start, Q 2)
    // with a narrow cloth rush (BP 1.5 kHz) on top; ≤ 0.4 s, mid-band.
    const start = 1050 * semis(c, 0) * (1 + 0.06 * c.v)
    return mix(c, 0.32, [
      { sig: burst(c.sr, 0.06, 0.001, 0.018, onePoleLp(c.sr, 420), rn(c), 1.6), gain: 0.8 },
      { at: 0.008 + 0.004 * c.v, sig: sweepNoise(c, 0.26, start, start * 1.5, 0.14, 2.0, 0.008, 0.06, 1) },
      { at: 0.012, sig: noiseBurst(c, 0.1, 0.008, 0.025, 1500 * semis(c, 0), 1.6, 0.4) },
    ])
  },
  land_plant: (c) => {
    // S10 touchdown: low thump (82 Hz sine, 60 ms) under a two-foot plant, then a short concrete slap 45 ms later.
    const thump = tone(c, 0.12, 82 * (1 + 0.04 * c.v), 0.002, 0.045, 'sine', 1.6)
    const plant = mix(c, 0.2, [{ sig: noiseBurst(c, 0.1, 0.001, 0.02, 350, 0.8, 0.5) }, { at: 0.03, sig: noiseBurst(c, 0.1, 0.001, 0.02, 300, 0.8, 0.4) }])
    const slap = burst(c.sr, 0.05, 0.0005, 0.012, biquad(c.sr, 'bp', 620 * semis(c, 0), 1.2), rn(c), 1.4)
    return mix(c, 0.3, [{ sig: thump }, { at: 0.005, sig: plant, gain: 0.6 }, { at: 0.045 + 0.005 * c.v, sig: slap, gain: 0.5 }])
  },
  plate_snap: (c) => mix(c, 0.3, [{ sig: partials(c, 0.15, [{ hz: 1900, amp: 0.5, dec: 0.03 }, { hz: 3100, amp: 0.3, dec: 0.02 }]) }, { at: 0.02, sig: noiseBurst(c, 0.2, 0.01, 0.05, 800, 0.9, 0.4) }]),
  servo_loop: (c) => { const n = Math.round(2 * c.sr); const out = new Float32Array(n); const f = bp(c.sr, 2400, 6); for (let i = 0; i < n; i++) out[i] = f(c.rng.bip()) * 0.5 + Math.sin(TAU * 1800 * i / c.sr + 0.5 * Math.sin(TAU * 0.7 * i / c.sr)) * 0.15; return out },
  plate_retract: (c) => mix(c, 0.3, [{ sig: noiseBurst(c, 0.2, 0.02, 0.05, 700, 0.9, 0.5) }, { at: 0.12, sig: partials(c, 0.12, [{ hz: 1500, amp: 0.35, dec: 0.03 }]) }]),
  clatter_exhale: (c) => { const hits = [0, 0.05, 0.09, 0.14].map((at, i) => ({ at, sig: partials(c, 0.12, [{ hz: 1700 - i * 150, amp: 0.4, dec: 0.03 }, { hz: 2800, amp: 0.2, dec: 0.02 }]) })); return mix(c, 0.6, [...hits, { at: 0.1, sig: noiseBurst(c, 0.45, 0.05, 0.15, 1500, 0.5, 0.5) }]) },
  inhale: (c) => { const n = noiseBurst(c, 0.35, 0.2, 0.08, 1800, 0.6, 0.5); return n },
  double_tick: (c) => mix(c, 0.2, [{ sig: noiseBurst(c, 0.04, 0.0004, 0.006, 3000, 2) }, { at: 0.08, sig: noiseBurst(c, 0.04, 0.0004, 0.006, 3000, 2) }]),
  pen_rev_full: (c) => derezUndraw(c),
  lpf_drop: (c) => { const out = buf(c, 0.45); const f = lp(c.sr, 90); for (let i = 0; i < out.length; i++) out[i] = f(c.rng.bip()) * 3 * ad(i, c.sr, 0.01, 0.12); return out },
  pen_fwd_full: (c) => penForward(c, 0.6, 10),
  tick_rub: (c) => mix(c, 0.3, [{ sig: noiseBurst(c, 0.03, 0.0003, 0.005, 5500, 2.2) }, { at: 0.04, sig: noiseBurst(c, 0.25, 0.05, 0.08, 2500, 0.5, 0.4) }]),
  rail_scuff: (c) => noiseBurst(c, 0.25, 0.03, 0.07, 900, 0.7, 0.4),
  click_pair: (c) => mix(c, 0.2, [{ sig: noiseBurst(c, 0.04, 0.0004, 0.006, 3500, 2) }, { at: 0.07, sig: noiseBurst(c, 0.04, 0.0004, 0.006, 4200, 2) }]),
  paper_slide: (c) => noiseBurst(c, 0.2, 0.04, 0.05, 2200, 0.5, 0.5),
  tick: (c) => noiseBurst(c, 0.04, 0.0004, 0.006, 4000, 2),
  tick_rev: (c) => reverse(noiseBurst(c, 0.05, 0.0004, 0.01, 3600, 2)),
  click_low: (c) => noiseBurst(c, 0.06, 0.0006, 0.012, 1200, 1.5),
  metronome: (c) => tone(c, 0.08, c.v ? 1760 : 1320, 0.001, 0.02, 'tri'),
  stamp: (c) => mix(c, 0.25, [{ sig: noiseBurst(c, 0.12, 0.001, 0.02, 500, 0.9) }, { sig: tone(c, 0.2, 110, 0.002, 0.05, 'sine', 0.6) }]),
  etch_pair: (c) => mix(c, 0.4, [{ sig: partials(c, 0.2, [{ hz: 2600, amp: 0.5, dec: 0.06 }, { hz: 3900, amp: 0.25, dec: 0.04 }]) }, { at: 0.12, sig: partials(c, 0.2, [{ hz: 2600, amp: 0.5, dec: 0.06 }, { hz: 3900, amp: 0.25, dec: 0.04 }]) }]),
  count_tick: (c) => metalTick(c, 880 * Math.pow(2, c.v / 12)),
  count_go: (c) => mix(c, 0.5, [{ sig: bellNote(c, 5) }, { sig: sweepNoise(c, 0.3, 800, 6000, 0.3, 1.2, 0.02, 0.12, 0.2) }]),
  timer_tick: (c) => mix(c, 0.2, [{ sig: tone(c, 0.12, c.v ? 1568 : 1046.5, 0.001, 0.03, 'tri') }, { sig: noiseBurst(c, 0.03, 0.0003, 0.005, 4500, 2, 0.4) }]),
  sting_two: (c) => mix(c, 0.9, [{ sig: bellNote(c, 0, 0.8) }, { at: 0.22, sig: bellNote(c, 5) }, { at: 0.22, sig: taiko(c, 0.8), gain: 0.7 }]),
  tag_rise: (c) => mix(c, 0.5, [{ sig: bellNote(c, -5, 0.7), gain: 0.8 }, { at: 0.12, sig: bellNote(c, 2) }]),
  tag_fall: (c) => mix(c, 0.5, [{ sig: bellNote(c, 2, 0.7), gain: 0.8 }, { at: 0.12, sig: bellNote(c, -5) }]),
  sting_low: (c) => {
    // Overtime: one low bell + the drone's first partial (55 Hz) fading in over 400 ms.
    const out = buf(c, 1.2)
    addInto(out, bellNote(c, -24), 0)
    for (let i = 0; i < out.length; i++) { const t = i / c.sr; out[i] += Math.sin(TAU * 55 * t) * 0.7 * Math.min(1, t / 0.4) * Math.min(1, (1.2 - t) / 0.3) }
    return out
  },
  pen_rev_slow_loop: (c) => { const a = penReverse(c, 2, 6); const b = penReverse(c, 2, 5); const out = new Float32Array(Math.round(4 * c.sr)); out.set(a.subarray(0, Math.min(a.length, out.length))); const o = Math.round(2 * c.sr); for (let i = 0; i < b.length && o + i < out.length; i++) out[o + i] += b[i]; for (let i = 0; i < out.length; i++) out[i] *= 0.4; return out },
  flat_tick: (c) => tone(c, 0.25, 440, 0.001, 0.08, 'tri'),
  sting_void: (c) => mix(c, 1.2, [{ sig: derezUndraw(c) }, { at: 0.01, sig: derezUndraw(c), gain: 0.8 }, { at: 0.3, sig: tone(c, 0.9, 233, 0.05, 0.3, 'tri', 0.5) }, { at: 0.3, sig: tone(c, 0.9, 246.9, 0.05, 0.3, 'tri', 0.5) }]),
  coin_tick: (c) => noiseBurst(c, 0.05, 0.0004, 0.008, 4600, 2),
  coin_land: (c) => mix(c, 0.4, [{ sig: noiseBurst(c, 0.15, 0.001, 0.03, 450, 0.9) }, { sig: tone(c, 0.3, 98, 0.002, 0.08, 'sine', 0.6) }]),
  score_click: (c) => mix(c, 0.15, [{ sig: noiseBurst(c, 0.04, 0.0004, 0.007, 3200, 2) }, { sig: tone(c, 0.1, 1320, 0.001, 0.02, 'tri', 0.4) }]),
  sting_long: (c) => mix(c, 1.4, [{ sig: bellNote(c, 0, 0.7), gain: 0.8 }, { at: 0.25, sig: bellNote(c, 5, 0.8), gain: 0.9 }, { at: 0.5, sig: bellNote(c, 12) }, { at: 0.5, sig: taiko(c), gain: 0.8 }]),
  etch: (c) => partials(c, 0.3, [{ hz: 2800, amp: 0.5, dec: 0.08 }, { hz: 4300, amp: 0.25, dec: 0.05 }]),
  hold_in: (c) => mix(c, 0.35, [{ sig: tone(c, 0.3, 440, 0.05, 0.1, 'sine') }, { sig: noiseBurst(c, 0.05, 0.0005, 0.008, 3000, 2, 0.5) }]),
  hold_loop: (c) => { const n = Math.round(2 * c.sr); const out = new Float32Array(n); for (let i = 0; i < n; i++) out[i] = Math.sin(TAU * 440 * i / c.sr) * 0.5 + Math.sin(TAU * 442 * i / c.sr) * 0.5; return out },
  hold_out: (c) => mix(c, 0.35, [{ sig: tone(c, 0.3, 392, 0.05, 0.1, 'sine') }, { sig: noiseBurst(c, 0.05, 0.0005, 0.008, 2400, 2, 0.5) }]),
  room_tone: (c) => { const n = Math.round(12 * c.sr); const out = new Float32Array(n); const f = lp(c.sr, 220); const f2 = bp(c.sr, 120, 2); for (let i = 0; i < n; i++) { const w = c.rng.bip(); out[i] = f(w) * 2.5 + f2(w) * 0.8 } return out },
  hum: (c) => { const n = Math.round(12 * c.sr); const out = new Float32Array(n); for (let i = 0; i < n; i++) out[i] = Math.sin(TAU * 100 * i / c.sr) * 0.5 + Math.sin(TAU * 200 * i / c.sr) * 0.25 + Math.sin(TAU * 300 * i / c.sr) * 0.1; return out },
  hall: (c) => { const n = Math.round(12 * c.sr); const out = new Float32Array(n); const f = lp(c.sr, 600); const g = hp(c.sr, 80); for (let i = 0; i < n; i++) out[i] = g(f(c.rng.bip())) * 2 * (0.8 + 0.2 * Math.sin(TAU * 0.05 * i / c.sr)); return out },
  stem_tick: stemTick,
  stem_pulse: stemPulseLow,
  stem_kit: stemKit,
  stem_metal: stemMetal,
  stem_low: stemLowHits,
  stem_ride: stemRide,
  stem_drone: stemDrone,
  stem_hub: stemHub,
  stem_results: stemResults,
  riser: (c) => { const out = buf(c, 0.7); const f = bp(c.sr, 800, 1.2); for (let i = 0; i < out.length; i++) { const t = i / out.length; out[i] = f(c.rng.bip()) * t * t * 1.5 + Math.sin(TAU * (200 + 900 * t * t) * i / c.sr) * 0.25 * t } return out },
  bandpass_sting: (c) => { const s = mix(c, 0.5, [{ sig: partials(c, 0.4, [{ hz: 523, amp: 0.5, dec: 0.12 }, { hz: 784, amp: 0.3, dec: 0.1 }]) }]); const f = bp(c.sr, 1100, 0.8); for (let i = 0; i < s.length; i++) s[i] = f(s[i]) * 2; return s },
  bracket_open: (c) => mix(c, 0.2, [{ sig: noiseBurst(c, 0.04, 0.0004, 0.006, 3000, 2) }, { at: 0.05, sig: noiseBurst(c, 0.04, 0.0004, 0.006, 4000, 2) }]),
  bracket_close: (c) => mix(c, 0.2, [{ sig: noiseBurst(c, 0.04, 0.0004, 0.006, 4000, 2) }, { at: 0.05, sig: noiseBurst(c, 0.04, 0.0004, 0.006, 3000, 2) }]),
  slider_tick: (c) => noiseBurst(c, 0.03, 0.0003, 0.004, 4800, 2.2),
  bind_ok: (c) => mix(c, 0.25, [{ sig: tone(c, 0.1, 880, 0.001, 0.03, 'tri') }, { at: 0.07, sig: tone(c, 0.15, 1320, 0.001, 0.05, 'tri') }]),
  bind_warn: (c) => mix(c, 0.3, [{ sig: tone(c, 0.12, 660, 0.001, 0.04, 'tri') }, { at: 0.1, sig: tone(c, 0.15, 622, 0.001, 0.05, 'tri') }]),
  error_tick: (c) => mix(c, 0.25, [{ sig: tone(c, 0.08, 330, 0.001, 0.03, 'tri') }, { at: 0.1, sig: tone(c, 0.08, 330, 0.001, 0.03, 'tri') }]),
  unlock: (c) => noiseBurst(c, 0.06, 0.0004, 0.01, 4400, 2),
}

const cache = new Map<string, RenderedSound>()

/**
 * Render one variation of a manifest id (cached). `opts.trim = false` skips the manifest
 * `gainDb` — the offline tools render stems as unity masters and apply the trim at encode
 * (`assets/audio/stems/gains.json`), which the manifest mirrors for the live fallback.
 */
export function renderSound(id: SoundId, variation: number, sampleRate: number, opts: { trim?: boolean } = {}): RenderedSound {
  const trim = opts.trim ?? true
  const key = `${id}:${variation}:${sampleRate}:${trim ? 't' : 'u'}`
  const hit = cache.get(key)
  if (hit) return hit
  const d = SOUNDS[id]
  const c: Ctx = { sr: sampleRate, rng: new Rng(hash(id) ^ Math.imul(variation + 1, 0x85ebca6b)), v: variation }
  let sig = RECIPES[d.synth](c)
  // Length cap (loops are exact by construction).
  const maxLen = Math.round(d.maxS * sampleRate)
  if (!d.loop && sig.length > maxLen) sig = sig.subarray(0, maxLen)
  // Peak-normalise to −1 dBTP, then per-id trim.
  let peak = 0
  for (let i = 0; i < sig.length; i++) { const a = Math.abs(sig[i]); if (a > peak) peak = a }
  const g = peak > 0 ? (0.89 / peak) * (trim ? dbToGain(d.gainDb ?? 0) : 1) : 0
  const out = new Float32Array(sig.length)
  for (let i = 0; i < sig.length; i++) out[i] = sig[i] * g
  // Short fade-out on one-shots so there are no end clicks.
  if (!d.loop) { const f = Math.min(out.length, Math.round(0.004 * sampleRate)); for (let i = 0; i < f; i++) out[out.length - 1 - i] *= i / f }
  const r: RenderedSound = d.loop ? { channels: [out], loopStart: 0, loopEnd: out.length / sampleRate } : { channels: [out] }
  cache.set(key, r)
  return r
}

export function clearSynthCache(): void { cache.clear() }
